import {
  CustomIntegrationSettingsType,
  GetIntegrationAgencySettingsResponseTO,
  IntegrationStatus,
  RemoteLockSettings,
} from 'models/InternalIntegrationSetting';
import { useTranslation } from 'react-i18next';
import usePropertySetting from '../usePropertySetting';

export const NO_LOCK_OPTION_VALUE = 'no-lock';

const useLockAssignment = ({
  integrationAgencySettings,
  propertyUid,
  pagePropertyUids,
}: {
  integrationAgencySettings: GetIntegrationAgencySettingsResponseTO;
  propertyUid: string;
  pagePropertyUids: string[];
}) => {
  const { t } = useTranslation();

  const { isLoading, propertySetting } = usePropertySetting({
    enumId: integrationAgencySettings.enumId,
    propertyUid,
    pagePropertyUids,
  });

  const getCurrentLockId = () => {
    // If property is not active, return no lock option
    if (propertySetting?.status !== IntegrationStatus.ACTIVE) {
      return NO_LOCK_OPTION_VALUE;
    }

    switch (propertySetting?.customIntegrationSettings?.type) {
      case CustomIntegrationSettingsType.POINTCENTRAL_PROPERTY:
        return (
          propertySetting?.customIntegrationSettings?.assignedLock ??
          NO_LOCK_OPTION_VALUE
        );
      case CustomIntegrationSettingsType.REMOTELOCK_PROPERTY:
        return (
          propertySetting?.customIntegrationSettings?.assignedLock ??
          NO_LOCK_OPTION_VALUE
        );
      default:
        return NO_LOCK_OPTION_VALUE;
    }
  };

  const getLockOptions = () => {
    const noLockOption = {
      label: t('pageIntegrations.integrationDetails.noLock'),
      value: NO_LOCK_OPTION_VALUE,
    };

    const extractPointCentralLockOptions = (
      availableLocks: Record<string, number>,
    ) => {
      return Object.keys(availableLocks).map((lock) => {
        return {
          label: lock,
          value: String(availableLocks[lock]),
        };
      });
    };

    const extractRemoteLockLockOptions = (
      remoteLockSettings: RemoteLockSettings[],
    ) => {
      return remoteLockSettings.map((lock) => ({
        label: lock.lockName!,
        value: lock.lockId!,
        accessibleType: lock.accessibleType,
      }));
    };

    switch (
      integrationAgencySettings.agencySettings?.customIntegrationSettings?.type
    ) {
      case CustomIntegrationSettingsType.POINTCENTRAL:
        return [
          noLockOption,
          ...extractPointCentralLockOptions(
            integrationAgencySettings.agencySettings?.customIntegrationSettings
              ?.availableLocks ?? {},
          ),
        ];

      case CustomIntegrationSettingsType.REMOTELOCK:
        return [
          noLockOption,
          ...extractRemoteLockLockOptions(
            integrationAgencySettings.agencySettings?.customIntegrationSettings
              ?.remoteLockSettings ?? [],
          ),
        ];
      default:
        return [noLockOption];
    }
  };

  return {
    lockOptions: getLockOptions(),
    currentLockId: getCurrentLockId(),
    isLoading,
  };
};

export default useLockAssignment;
